'use client';

import React, { HTMLAttributes } from "react"
import { cn } from "@/lib/utils";

export const components = {
  h1: ({ className, children }: HTMLAttributes<HTMLHeadElement>) => (
    <h1
      className={cn(
        "mt-6 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0",
        className
      )}
    >{children}</h1>
  ),
  h2: ({ className, children }: HTMLAttributes<HTMLHeadElement>) => (
    <h2
      className={cn(
        "mt-3 scroll-m-20 text-base font-semibold tracking-tight first:mt-0",
        className
      )}
    >{children}</h2>
  ),
  h3: ({ className, children }: HTMLAttributes<HTMLHeadElement>) => (
    <h3
      className={cn(
        "mt-3 scroll-m-20 text-sm font-semibold",
        className
      )}
    >{children}</h3>
  ),
  h4: ({ className, children }: HTMLAttributes<HTMLHeadElement>) => (
    <h4
      className={cn(
        "mt-3 scroll-m-20 text-sm font-semibold",
        className
      )}
    >{children}</h4>
  ),
  h5: ({ className, children }: HTMLAttributes<HTMLHeadElement>) => (
    <h5
      className={cn(
        "mt-3 scroll-m-20 text-sm font-semibold ",
        className
      )}
    >{children}</h5>
  ),
  h6: ({ className, children }: HTMLAttributes<HTMLHeadElement>) => (
    <h6
      className={cn(
        "mt-3 scroll-m-20 text-sm font-semibold",
        className
      )}
    >{children}</h6>
  ),
  a: ({ className, children }: HTMLAttributes<HTMLAnchorElement>) => (
    <a
      className={cn("font-medium underline underline-offset-4", className)}
    >{children}</a>
  ),
  p: ({ className, children }: HTMLAttributes<HTMLParagraphElement>) => (
    <p
      className={cn("leading-6 text-sm [&:not(:first-child)]:mt-1 text-muted-foreground", className)}
    >{children}</p>
  ),
  ul: ({ className, children }: HTMLAttributes<HTMLUListElement>) => (
    <ul className={cn("my-3 mt-1 ml-6 list-disc", className)}>{children}</ul>
  ),
  ol: ({ className, children, ordered, ...rest }: HTMLAttributes<HTMLOListElement> & {
    ordered?: boolean;
  }) => {
    return (
      <ol className={cn("my-3 mt-1 ml-6 list-decimal", className)} {...rest}>{children}</ol>
    )
  },
  li: ({ className, children }: HTMLAttributes<HTMLLIElement>) => (
    <li className={cn("mt-1 text-muted-foreground", className)} >{children}</li>
  ),
  strong: ({ className, children }: HTMLAttributes<HTMLElement>) => (
    <strong className={cn("text-foreground font-medium", className)} >{children}</strong>
  ),
  blockquote: ({ className, children }: HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote
      className={cn(
        "mt-6 border-l-2 pl-6 italic [&>*]:text-muted-foreground",
        className
      )}
    >{children}</blockquote>
  ),
  table: ({ className, children }: HTMLAttributes<HTMLTableElement>) => (
    <table className={cn("w-full border", className)}>{children}</table>
  ),
  thead: ({ className, children }: HTMLAttributes<HTMLTableSectionElement>) => (
    <thead className={cn("[&_th]:border-b [&_th:last-child]:border-b", className)}>{children}</thead>
  ),
  tbody: ({ className, children }: HTMLAttributes<HTMLTableSectionElement>) => (
    <tbody className={cn("[&_tr]:border-b [&_tr:last-child]:border-b", className)}>{children}</tbody>
  ),
  tr: ({ className, children }: HTMLAttributes<HTMLTableRowElement>) => (
    <tr
      className={cn(
        "transition-colors",
        className
      )}
    >{children}
    </tr>
  ),
  th: ({ className, children }: HTMLAttributes<HTMLTableCellElement>) => (
    <th
      className={cn(
        "border-r h-12 px-4 text-left align-middle font-medium bg-muted",
        className
      )}
    >{children}</th>
  ),
  td: ({ className, children }: HTMLAttributes<HTMLTableCellElement>) => (
    <td className={cn("border-r p-4 align-top", className)}>{children}</td>
  ),
}

export const externalMarkdownLink = {
  a: ({ className, children, ...props }: HTMLAttributes<HTMLAnchorElement>) => (
    <a
      className={cn("text-primary hover:underline underline-offset-4", className)}
      {...props}
      target="_blank"
    >{children}</a>
  ),
  p: ({ children, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
    <p {...props}>{children}</p>
  ),
  strong: ({ className, children, ...props }: HTMLAttributes<HTMLElement>) => (
    <strong className={cn("font-medium", className)} {...props}>{children}</strong>
  ),
}